import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, MapPin, Package, Plug, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import useAuthStore from '../../lib/auth-store';
import { apiFetch } from '../merchant/attributionApi';

const STEPS = [['entity', 'Legal entity', Building2], ['location', 'Location', MapPin], ['product', 'Product', Package], ['provider', 'Processor', Plug]];

function Choice({ active, onClick, title, sub, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`w-full text-left rounded-lg border p-3 transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
      <p className="text-sm font-medium flex items-center gap-2">{active && <Check className="w-3.5 h-3.5 text-primary" />}{title}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </button>
  );
}

export default function StartBoardingDialog({ groupId, open, onClose }) {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ entity: '', location: '', product: '', conn: '', platform: '', opportunity: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) apiFetch(token, `/boarding/wizard/options?merchant_group_id=${groupId}`).then(setOpts).catch((e) => toast.error(e.message)); }, [open, token, groupId]);
  if (!opts) return null;
  const entity = opts.entities.find((e) => e.id === f.entity);
  const pairs = opts.connections.flatMap((c) => c.platforms.filter((p) => opts.templates.some((t) => t.platform === p && t.provider_slug === c.provider_slug && t.application_type === (f.product || 'merchant_processing')))
    .map((p) => ({ conn: c, platform: p, template: opts.templates.find((t) => t.platform === p && t.provider_slug === c.provider_slug) })));
  const done = [f.entity, f.location, f.product, f.conn][step];

  const start = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(token, '/boarding/applications', 'POST', {
        merchant_group_id: groupId, legal_entity_id: f.entity, location_id: f.location, product: f.product,
        provider_account_id: f.conn, platform: f.platform, opportunity_id: f.opportunity || undefined,
      });
      toast.success('Application started from the Merchant Vault');
      navigate(`/boarding/${res.application_id}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="start-boarding-dialog">
        <DialogHeader>
          <DialogTitle>Start new application</DialogTitle>
          <DialogDescription>Uses this merchant's existing vault records — nothing is retyped or duplicated.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 mb-2">
          {STEPS.map(([k, label, Icon], i) => (
            <div key={k} className={`flex-1 text-[11px] flex items-center gap-1 px-2 py-1 rounded ${i === step ? 'bg-primary text-primary-foreground' : i < step ? 'bg-primary/10' : 'bg-muted'}`}>
              <Icon className="w-3 h-3" /> <span className="hidden sm:inline">{label}</span>
            </div>
          ))}
        </div>
        <div className="space-y-2 max-h-80 overflow-auto">
          {step === 0 && opts.entities.map((e) => <Choice key={e.id} active={f.entity === e.id} onClick={() => setF({ ...f, entity: e.id, location: '' })} title={e.name} sub={e.dba} testId={`wizard-entity-${e.id}`} />)}
          {step === 1 && (entity?.locations || []).map((l) => (
            <Choice key={l.id} active={f.location === l.id} onClick={() => setF({ ...f, location: l.id })} title={l.name} testId={`wizard-location-${l.id}`}
                    sub={l.processing_accounts.length ? l.processing_accounts.map((p) => `${p.processor} …${p.mid_last4} (${p.status})`).join(', ') : 'No processing accounts yet'} />
          ))}
          {step === 2 && opts.products.map((p) => <Choice key={p.id} active={f.product === p.id} onClick={() => setF({ ...f, product: p.id })} title={p.name} testId={`wizard-product-${p.id}`} />)}
          {step === 3 && (pairs.length ? pairs.map(({ conn, platform, template }) => (
            <Choice key={`${conn.id}-${platform}`} active={f.conn === conn.id && f.platform === platform} onClick={() => setF({ ...f, conn: conn.id, platform })}
                    title={`${platform.toUpperCase()} · ${template.vendor_name} template v${template.version}`} sub={`${conn.label} · ${conn.environment}`} testId={`wizard-provider-${conn.id}-${platform}`} />
          )) : <p className="text-sm text-muted-foreground" data-testid="wizard-no-connections">No active processor connection. Connect one under Processor Connections first.</p>)}
          {step === 3 && opts.opportunities.length > 0 && (
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-1">Link to opportunity (optional)</p>
              {opts.opportunities.map((o) => <Choice key={o.id} active={f.opportunity === o.id} onClick={() => setF({ ...f, opportunity: f.opportunity === o.id ? '' : o.id })} title={o.name} testId={`wizard-opportunity-${o.id}`} />)}
            </div>
          )}
        </div>
        <DialogFooter>
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-back">Back</Button>}
          {step < 3 ? <Button disabled={!done} onClick={() => setStep(step + 1)} data-testid="wizard-next">Next</Button>
            : <Button disabled={!f.conn || busy} onClick={start} data-testid="wizard-start">{busy ? 'Loading template…' : 'Load application'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
